/**
 * AccountInfoForm Component
 * Fatura için cari bilgileri formu
 */

import React from 'react';
import {
  Grid,
  TextField,
  Button,
  Box,
} from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { useTranslation } from 'react-i18next';
import { Account } from '../types/invoice.types';
import { InvoiceSection, SectionTitle } from '../styled/InvoiceStyledComponents';

interface AccountInfoFormProps {
  selectedAccount: Account | null;
  onOpenAccountModal: () => void;
  disabled?: boolean;
  onAccountDataChange?: (field: keyof Account, value: string) => void;
  isEditMode?: boolean;
}

export const AccountInfoForm: React.FC<AccountInfoFormProps> = ({
  selectedAccount,
  onOpenAccountModal,
  disabled = false,
  onAccountDataChange,
  isEditMode = false,
}) => {
  const { i18n } = useTranslation();
  const isTr = i18n.language.startsWith('tr');
  const tt = (trText: string, enText: string) => (isTr ? trText : enText);

  // Cari seçildiyse ve callback varsa alanlar her zaman düzenlenebilir (fatura snapshot'ı için)
  const isEditable = !!selectedAccount && !!onAccountDataChange && !disabled;

  // Debug için render logları
  console.log('🔍 [AccountInfoForm] Render:', {
    hasAccount: !!selectedAccount,
    hasCallback: !!onAccountDataChange,
    isDisabled: disabled,
    isEditMode,
    isEditable,
    accountAddress: selectedAccount?.address
  });

  return (
    <InvoiceSection>
      <SectionTitle variant="h6" sx={{ fontSize: '1rem', mb: 1 }}>
        {tt('Cari Bilgileri', 'Account Information')}
      </SectionTitle>
      
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              fullWidth
              label={tt('Cari Hesap', 'Account')}
              value={selectedAccount?.companyName || ''}
              InputProps={{
                readOnly: true,
              }}
              placeholder={tt('Cari hesap seçin', 'Select account')}
              size="small"
              required
            />
            <Button
              variant="contained"
              color="primary"
              onClick={onOpenAccountModal}
              disabled={disabled}
              startIcon={<PersonAddIcon />}
              sx={{ minWidth: '120px' }}
            >
              {tt('Seç', 'Select')}
            </Button>
          </Box>
        </Grid>
        
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            label={tt('Vergi No', 'Tax ID')}
            value={selectedAccount?.taxNumber || ''}
            onChange={(e) => {
              if (isEditable && onAccountDataChange) {
                onAccountDataChange('taxNumber', e.target.value);
              }
            }}
            InputProps={{
              readOnly: !isEditable,
            }}
            disabled={disabled}
            size="small"
          />
        </Grid>
        
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            label={tt('Vergi Dairesi', 'Tax Office')}
            value={selectedAccount?.taxOffice || ''}
            onChange={(e) => {
              if (isEditable && onAccountDataChange) {
                onAccountDataChange('taxOffice', e.target.value);
              }
            }}
            InputProps={{
              readOnly: !isEditable,
            }}
            disabled={disabled}
            size="small"
          />
        </Grid>
        
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            label={tt('Telefon', 'Phone')}
            value={selectedAccount?.phone || ''}
            onChange={(e) => {
              if (isEditable && onAccountDataChange) {
                onAccountDataChange('phone', e.target.value);
              }
            }}
            InputProps={{
              readOnly: !isEditable,
            }}
            disabled={disabled}
            size="small"
          />
        </Grid>
        
        <Grid item xs={12}>
          <TextField
            fullWidth
            label={tt('Adres', 'Address')}
            value={selectedAccount?.address || ''}
            onChange={(e) => {
              console.log('📝 [ADRES DEĞİŞTİ] Yeni değer:', e.target.value, 'isEditable:', isEditable);
              if (isEditable && onAccountDataChange) {
                console.log('✅ [ADRES] onAccountDataChange çağrılıyor...');
                onAccountDataChange('address', e.target.value);
              } else {
                console.warn('⚠️ [ADRES] onAccountDataChange çağrılamadı!', { isEditable, hasCallback: !!onAccountDataChange });
              }
            }}
            InputProps={{
              readOnly: !isEditable,
            }}
            disabled={disabled}
            multiline
            rows={2}
            size="small"
          />
        </Grid>
        
      </Grid>
    </InvoiceSection>
  );
};

